<!-- Chat template -->
<template>
  <div>
    <!-- Chat Toggle Button -->
    <button
      class="btn btn-outline-success mb-3 chat-toggle-button"
      @click="showChat = !showChat"
      v-if="chatType !== 'search' || !isChatFullscreen"
    >
      {{ title }}
    </button>

    <!-- Main Chat Container -->
    <div
      v-if="showChat"
      class="chat-recipe-container"
      :class="{
        'mt-2 mb-4 p-3 border rounded': !isChatFullscreen,
        'fullscreen-chat': isChatFullscreen,
      }"
    >
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">{{ title }}</h5>
        <div class="chat-controls">
          <button
            @click="closeChat"
            class="btn btn-sm btn-icon-close me-2"
            title="Закрыть чат"
          >
            <i class="bi bi-x-lg"></i>
          </button>
          <button
            @click="toggleChatFullscreen"
            class="btn btn-sm btn-icon-fullscreen"
            :title="isChatFullscreen ? 'Свернуть чат' : 'Чат на весь экран'"
          >
            <i
              :class="
                isChatFullscreen
                  ? 'bi bi-arrows-angle-contract'
                  : 'bi bi-arrows-fullscreen'
              "
            ></i>
          </button>
        </div>
      </div>
      <div
        v-if="isLoadingMoreHistory"
        class="text-center my-1 small text-muted"
      >
        <span
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Загрузка предыдущих сообщений...
      </div>

      <div class="chat-messages mb-3" ref="chatMessagesContainer">
        <div
          v-for="msg in messages"
          :key="msg.id || msg.timestamp"
          class="message mb-2"
          :class="{
            'user-message-row': msg.sender === 'user',
            'assistant-message-row': msg.sender === 'assistant',
          }"
        >
          <img
            v-if="msg.sender === 'assistant'"
            src="../assets/cooksy-avatar.png"
            alt="Cooksy"
            class="chat-avatar assistant-avatar me-2"
          />
          <span
            class="message-bubble p-2 rounded d-inline-block position-relative"
            :class="{
              'user-message-bubble': msg.sender === 'user',
              'assistant-message-bubble': msg.sender === 'assistant',
            }"
          >
            <button
              v-if="msg.sender === 'assistant'"
              @click="copyToClipboard(msg.message)"
              class="btn btn-sm btn-copy-message position-absolute top-0 end-0 m-1"
              title="Копировать"
            >
              <i class="bi bi-clipboard"></i>
            </button>
            <div
              class="message-content"
              v-html="renderMarkdown(msg.message)"
            ></div>
          </span>
          <!-- Placeholder for user avatar if needed in the future -->
          <!-- <img v-if="msg.sender === 'user'" src="../assets/user-avatar-placeholder.png" alt="User" class="chat-avatar user-avatar ms-2"> -->
        </div>
      </div>

      <div class="input-group">
        <textarea
          class="form-control"
          id="chat-input-textarea"
          v-model="newMessage"
          @input="adjustTextareaHeight"
          @keyup.enter.exact.prevent="handleEnterKey"
          :placeholder="placeholder"
          :disabled="isLoading"
          rows="1"
          ref="newMessageTextarea"
          maxlength="2000"
          style="resize: none; overflow-y: hidden"
        />
        <button
          class="btn btn-primary btn-send-icon"
          @click="sendMessage"
          :disabled="isLoading || !newMessage.trim()"
          title="Отправить"
        >
          <span
            v-show="isLoading"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          <i v-show="!isLoading" class="bi bi-send-fill"></i>
        </button>
      </div>

      <div
        v-if="errorMessage && !isChatFullscreen"
        class="text-danger mt-2 small"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ChatRecipe",
  props: {
    recipeId: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    id: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    chatType: {
      type: String,
      required: false,
      default: "recipe",
    },
    title: {
      type: String,
      default: "Помощь в приготовлении",
    },
    placeholder: {
      type: String,
      required: false,
      default: "Спросите что-нибудь о рецепте...",
    },
  },
  data() {
    return {
      showChat: false, // Control chat visibility internally
      messages: [], // Stores chat messages: { sender: 'user'/'assistant', message: 'text', timestamp: Date }
      newMessage: "",
      isLoading: false,
      errorMessage: null,
      isChatFullscreen: false,
      initialTextareaHeight: "auto",
      // Pagination for history
      historyPageSize: 20, // Number of messages to fetch per page
      currentHistoryOffset: 0,
      isLoadingMoreHistory: false,
      canLoadMoreHistory: true,
      scrollListenerAttached: false,
    };
  },

  methods: {
    /**
     * Closes the chat window.
     */
    closeChat() {
      this.showChat = false;
      // If the chat was in fullscreen, we need to reset this state
      // and update the stored value to prevent it from reopening in fullscreen.
      if (this.isChatFullscreen) {
        this.isChatFullscreen = false;
        this.$emit("fullscreen-toggled", false);
      }
      this.setStoredFullscreenState(false);
    },

    /**
     * Load chat history from server
     *
     * @returns {Promise<void>}
     */
    async loadHistory() {
      // Initial load of history
      this.isLoading = true;
      this.errorMessage = null;
      this.messages = [];
      this.currentHistoryOffset = 0;
      this.canLoadMoreHistory = true;

      await this.fetchHistoryPage(0);

      this.isLoading = false;
    },

    /**
     * Fetches a page of chat history from the server.
     * @param {number} offset - The offset for fetching messages.
     */
    async fetchHistoryPage(offset) {
      if (!this.canLoadMoreHistory && offset > 0) return;

      if (offset > 0) {
        this.isLoadingMoreHistory = true;
      }
      // For offset === 0, general isLoading is handled by loadHistory

      try {
        let idForRequest = this.id || this.recipeId;
        if (this.chatType === "recipe") {
          idForRequest = this.recipeId;
        }
        if (
          !idForRequest &&
          this.chatType !== "about" &&
          this.chatType !== "suggestions"
        )
          throw new Error("Chat ID or Recipe ID is required.");

        const command = `chat.history/${this.chatType}/${idForRequest}/${this.historyPageSize}/${offset}`;
        const serverResponseRaw = await this.sendCmd(command);

        const serverResponseObject = JSON.parse(serverResponseRaw);
        const historyDecompressed = await this.unpackData(
          // Assumes this.unpackData is globally available
          serverResponseObject.data
        );
        const newMessages = JSON.parse(historyDecompressed).map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));

        if (newMessages.length > 0) {
          const chatContainer = this.$refs.chatMessagesContainer;
          const oldScrollHeight = chatContainer
            ? chatContainer.scrollHeight
            : 0;
          const oldScrollTop = chatContainer ? chatContainer.scrollTop : 0;

          if (offset === 0) {
            this.messages = newMessages.slice().reverse();
          } else {
            this.messages = [
              ...newMessages.slice().reverse(),
              ...this.messages,
            ];
          }

          this.$nextTick(() => {
            if (chatContainer && offset > 0) {
              // Restore scroll position relative to the old top message
              chatContainer.scrollTop =
                oldScrollTop + (chatContainer.scrollHeight - oldScrollHeight);
            }
            // For offset === 0, scrollToBottom is handled by loadHistory
          });
        }

        this.currentHistoryOffset = offset + newMessages.length;
        this.canLoadMoreHistory = newMessages.length === this.historyPageSize;
      } catch (error) {
        console.error("Error loading chat history:", error);
        this.errorMessage = "Не удалось загрузить страницу истории чата.";
        if (offset > 0) this.canLoadMoreHistory = false;
      } finally {
        if (offset > 0) {
          this.isLoadingMoreHistory = false;
        }
        // For offset === 0, isLoading is handled by loadHistory
      }
    },

    /**
     * Sends a message to the server and processes the response.
     * @fires loadHistory - After receiving the server response.
     */
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      const userMessage = {
        sender: "user",
        message: this.newMessage,
        timestamp: new Date(),
      };
      this.messages.push(userMessage);
      const currentMessageText = this.newMessage;
      this.newMessage = "";
      this.isLoading = true;
      this.errorMessage = null;
      this.scrollToBottom();
      this.$nextTick(() => {
        this.resetTextareaHeight();
      });

      try {
        let idForRequest = this.id || this.recipeId;
        if (this.chatType === "recipe") {
          idForRequest = this.recipeId;
        }
        if (
          !idForRequest &&
          this.chatType !== "about" &&
          this.chatType !== "suggestions"
        )
          throw new Error(
            "Chat ID or Recipe ID is required for sending a message."
          );

        /**
         * Send the message to the server.
         * @param {string} command - The command for the chat.
         * @param {string} data - Optional data to send with the command.
         * @returns {Promise<string>} The server response.
         */
        const responseRaw = await this.sendCmd(
          `chat.message/${this.chatType}/${idForRequest}`,
          JSON.stringify({ user_message: currentMessageText })
          // No need to send chat_history, server loads it from DB
        );
        const response = JSON.parse(responseRaw);
        if (response && response.assistant_response) {
          this.messages.push({
            sender: "assistant",
            message: response.assistant_response,
            timestamp: new Date(),
          });
        }
        this.scrollToBottom();
        /**
         * Emits the "loadHistory" event after sending the message and processing the response.
         * @event loadHistory
         * @type {void}
         */
        this.$emit("loadHistory");
      } catch (error) {
        console.error("Error sending message:", error);
        this.errorMessage =
          "Ошибка отправки сообщения: " + (error.message || error);
        // Optionally, add the failed user message back to input or mark as failed
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Scrolls the chat container to the bottom.
     * @fires $nextTick - Waits for the next tick to ensure the container is rendered.
     * @param {HTMLElement} container - The container element.
     */
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    /**
     * Toggles the chat container to fullscreen.
     * @fires $emit - Emits the "fullscreen-toggled" event.
     * @param {Boolean} isFullscreen - The new fullscreen state.
     */
    toggleChatFullscreen(event) {
      if (event) {
        event.stopPropagation();
      }
      this.isChatFullscreen = !this.isChatFullscreen;
      this.$emit("fullscreen-toggled", this.isChatFullscreen);
      this.setStoredFullscreenState(this.isChatFullscreen);

      // If we just exited fullscreen, scroll the component into view to ensure
      // it's visible, especially if the user has scrolled the page while in
      // fullscreen mode.
      if (!this.isChatFullscreen) {
        this.$nextTick(() => {
          if (this.$el && typeof this.$el.scrollIntoView === "function") {
            this.$el.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        });
      }
    },

    /**
     * Retrieves the stored fullscreen state for the current recipe.
     * @returns {Boolean} The stored fullscreen state.
     */
    getStoredFullscreenState() {
      // Retrieve the stored state from local storage
      const state = localStorage.getItem(
        // Use a key specific to the recipe ID
        `cooksyChat_${this.chatType}_${this.recipeId}`
      );
      // Return the stored state as a boolean
      return state === "true";
    },

    /**
     * Stores the fullscreen state for the current recipe in local storage.
     * @param {Boolean} isFullscreen - The new fullscreen state to store.
     */
    setStoredFullscreenState(isFullscreen) {
      // if (!this.recipeId) return; // No recipe ID, so no storage
      // Store the state in local storage using a recipe-specific key
      localStorage.setItem(
        `cooksyChat_${this.chatType}_${this.recipeId}`,
        isFullscreen ? "true" : "false"
      );
    },

    /**
     * Adjusts the height of the new message textarea to fit its content.
     * @see https://stackoverflow.com/a/25621205/222310
     */
    adjustTextareaHeight() {
      const textarea = this.$refs.newMessageTextarea;
      if (textarea) {
        // Reset height to allow shrinking
        textarea.style.height = "auto";
        // Set new height based on content
        textarea.style.height = `${textarea.scrollHeight}px`;
        // Max height will be controlled by CSS (e.g. to prevent overflow)
      }
    },

    /**
     * Resets the height of the new message textarea to its initial height.
     * This is needed after sending a message, when the textarea is cleared.
     */
    resetTextareaHeight() {
      const textarea = this.$refs.newMessageTextarea;
      if (textarea) {
        // Reset to the initial height, which was set in the mounted() hook
        textarea.style.height = this.initialTextareaHeight;
      }
    },

    /**
     * Sends the message in the new message textarea when Enter is pressed.
     * Shift+Enter is allowed for new lines (default textarea behavior).
     */
    handleEnterKey() {
      // Send on Enter, allow Shift+Enter for new lines (default textarea behavior)
      this.sendMessage();
    },

    /**
     * Handles the scroll event for the chat messages container.
     * If the user has scrolled to the very top of the container and more history is available,
     * fetch the next page of messages.
     */
    handleScroll() {
      const container = this.$refs.chatMessagesContainer;
      if (
        container &&
        container.scrollTop === 0 && // Scrolled to the very top
        this.canLoadMoreHistory &&
        !this.isLoadingMoreHistory
      ) {
        // Fetch the next page of messages
        this.fetchHistoryPage(this.currentHistoryOffset);
      }
    },

    /**
     * Handles clicks outside the component to close the chat window.
     * @param {MouseEvent} event - The click event.
     */
    handleClickOutside(event) {
      // Do nothing if chat is fullscreen or not shown
      if (this.isChatFullscreen || !this.showChat) {
        return;
      }

      // Close chat if the click is outside the component's root element
      if (this.$el && !this.$el.contains(event.target)) {
        // A small delay to allow parent buttons to toggle state if needed
        if (this.chatType !== "search") {
          setTimeout(() => (this.showChat = false), 50);
        }
      }
    },

    /**
     * Copies the given text to the user's clipboard.
     * @param {string} text The text to copy.
     * @returns {Promise<void>} A promise that resolves when the text is copied.
     */
    async copyToClipboard(text) {
      try {
        // Use the modern navigator.clipboard API to copy the text
        await navigator.clipboard.writeText(text);
        // Optionally, show a temporary "Copied!" message or change icon
        console.log("Message copied to clipboard");
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    },

    /**
     * Render Markdown text as HTML.
     * @param {string} text The Markdown text to render.
     * @returns {string} The rendered HTML.
     */
    renderMarkdown(text) {
      if (!text) return "";

      // Process images first: !alt text
      let processedText = text.replace(
        /!\[(.*?)\]\((.*?)\)/g,
        (match, alt, src) => {
          let imageUrl = src;
          // Prepend API URL for our internal images
          if (src.startsWith("/image.get/") && this.$globalState.apiUrl) {
            imageUrl = this.$globalState.apiUrl + src;
          }
          return `<img src="${imageUrl}" alt="${alt}" class="img-fluid rounded my-2" style="max-height: 200px; max-width: 100%;">`;
        }
      );

      processedText = processedText
        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>') // Links
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold
        .replace(/\*(.*?)\*/g, "<em>$1</em>") // Italic
        .replace(/`([^`]+)`/g, "<code>$1</code>") // Inline code
        .replace(/\n/g, "<br>");

      return processedText;
    },
  },

  mounted() {
    // If this is a 'search' chat, open it immediately.
    // For other chat types, respect the stored fullscreen state.
    if (
      this.chatType === "search" ||
      (this.chatType !== "search" && this.getStoredFullscreenState())
    ) {
      this.showChat = true;
    }

    console.debug("ChatRecipe mounted. chatType =", this.chatType);

    this.loadHistory();
    this.isChatFullscreen = this.getStoredFullscreenState();
    this.$emit("fullscreen-toggled", this.isChatFullscreen);

    this.$nextTick(() => {
      const textarea = this.$refs.newMessageTextarea;
      if (textarea) {
        this.initialTextareaHeight = `${textarea.scrollHeight}px`;
        textarea.style.height = this.initialTextareaHeight;
      }
    });
  },

  beforeUnmount() {
    const chatContainer = this.$refs.chatMessagesContainer;
    if (chatContainer) {
      chatContainer.removeEventListener("scroll", this.handleScroll);
      document.removeEventListener("click", this.handleClickOutside);
    }
  },

  watch: {
    /**
     * When the chat becomes visible, scroll to the bottom to show the latest messages.
     * We use nextTick within scrollToBottom to ensure the chat container is rendered
     * before we attempt to scroll.
     */
    showChat(isShown) {
      if (isShown) {
        this.scrollToBottom();
        // After the chat window is shown and rendered, attach the scroll listener
        // for lazy-loading history. We use a flag to ensure it's only attached once
        // per element instance.
        this.$nextTick(() => {
          const container = this.$refs.chatMessagesContainer;
          if (container && !this.scrollListenerAttached) {
            container.addEventListener("scroll", this.handleScroll);
            this.scrollListenerAttached = true;
            // Add listener for clicks outside
            document.addEventListener("click", this.handleClickOutside);
          }
        });
      } else {
        // When the chat is hidden, the DOM element is destroyed. We reset the flag
        // so the listener can be re-attached when the chat is shown again.
        this.scrollListenerAttached = false;
        // Remove listener for clicks outside
        document.removeEventListener("click", this.handleClickOutside);
      }
    },

    /**
     * Watches for changes in recipeId and reacts accordingly.
     * If recipeId changes, this component will:
     *   - Clear all messages
     *   - Clear any previous errors
     *   - Clear new message input
     *   - Reset textarea height
     *   - If new recipeId is valid, load its chat history
     *     and set stored fullscreen state
     *   - If no recipeId is provided, stop loading
     */
    recipeId(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Reset chat visibility state when recipe changes
        if (this.getStoredFullscreenState()) {
          this.showChat = true;
        } else {
          this.showChat = false;
        }
        this.messages = []; // Clear messages for new recipe
        this.errorMessage = null; // Clear previous errors
        this.newMessage = ""; // Clear new message input
        this.resetTextareaHeight(); // Reset textarea height

        if (newVal) {
          // Only load history if new recipeId is valid
          this.$nextTick(() => {
            // Ensure DOM is updated and props are settled
            this.loadHistory();
            this.isChatFullscreen = this.getStoredFullscreenState();
            this.$emit("fullscreen-toggled", this.isChatFullscreen);
          });
        } else {
          this.isLoading = false; // If no recipeId, stop loading
        }
      }
    },

    /**
     * Watches for programmatic changes to newMessage if any
     * If newMessage is cleared (e.g. by sending a message), reset textarea height
     */
    newMessage(newVal) {
      if (newVal === "") {
        // If message is cleared, reset textarea height
        this.resetTextareaHeight();
      }
    },
  },
};
</script>

<style scoped>
.chat-recipe-container {
  max-width: 820px;
}
.chat-messages {
  height: 300px; /* Or your preferred height */
  overflow-y: auto;
  padding: 10px;
}
.message-bubble {
  max-width: 80%;
  word-wrap: break-word;
}

.fullscreen-chat {
  position: fixed;
  top: 59px;
  width: 100vw;
  height: 100vh;
  background-color: var(--bs-body-bg, white);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  margin-left: -12px;
  padding: 10px;
  padding-bottom: 70px;
}

.fullscreen-chat .chat-messages {
  flex-grow: 1;
  height: auto !important; /* Override fixed height */
  margin-bottom: 0 !important; /* Remove bottom margin */
}

.btn-icon-fullscreen {
  padding: 0.25rem 0.5rem;
  font-size: 1rem;
  line-height: 1;
}

.btn-icon-close {
  padding: 0.25rem 0.5rem;
  font-size: 1.2rem; /* Slightly larger for better click target */
  line-height: 1;
  color: var(--bs-secondary-color);
}

.btn-send-icon {
  /* Adjust padding if necessary to align icon nicely with textarea */
}

/* Style for the textarea */
#chat-input-textarea {
  line-height: 1.5; /* Adjust for better readability */
  min-height: calc(
    1.5em + 0.75rem + 2px
  ); /* Approx height of a single line input (Bootstrap's default form-control padding and border) */
  max-height: calc(
    1.5em * 10 + 0.75rem + 2px
  ); /* Max height for ~10 lines, then scroll. Adjust 10 as needed. */
  overflow-y: auto; /* Show scrollbar when max-height is exceeded */
}

.message {
  display: flex;
  align-items: flex-end; /* Align avatar and bubble to the bottom */
}

.user-message-row {
  justify-content: flex-end;
}

.assistant-message-row {
  justify-content: flex-start;
}

.user-message-bubble {
  background-color: var(
    --bs-secondary-bg
  ); /* A theme-aware grey, distinct from assistant */
  color: var(--bs-body-color); /* Theme-aware text color for better contrast */
  text-align: left; /* Ensure text inside user bubble is left-aligned */
}

.assistant-message-bubble {
  background-color: var(
    --bs-tertiary-bg
  ); /* Theme-aware subtle background, good for light/dark modes */
  color: var(--bs-body-color); /* Theme-aware text color */
}

.chat-avatar {
  width: 40px; /* Adjust size as needed */
  height: 40px; /* Adjust size as needed */
  border-radius: 50%; /* Make it circular */
}

.btn-copy-message {
  padding: 0.1rem 0.3rem;
  font-size: 0.8rem;
  line-height: 1;
  opacity: 0.3; /* Make it less prominent until hover/focus */
  z-index: 1; /* Ensure it's above the message content for clicking */
}

.btn-copy-message:hover,
.btn-copy-message:focus {
  opacity: 1;
}

.message-content {
  /* Ensures that the copy button doesn't overlap with the start of the text too much */
}

.search-chat .chat-toggle-button {
  display: none;
}
</style>
